import * as React from 'react';
import { t } from '@citrite/translate';
import {
	AlertErrorIcon,
	AlertWarningIcon,
	layout,
	palette,
	Tooltip,
	TooltipIcon,
	typography,
} from '@citrite/web-ui-component';
import styled from '@emotion/styled';
import { FlexRow } from '../ui/Container';

export type FilterType = 'all' | 'expired' | 'expiring';

interface CategoryKPIProps {
	title: string;
	count: number;
	StatusIcon: JSX.Element;
	helpText: string;
	isSelected?: boolean;
	onClick?: () => void;
}

const Subtitle = styled.h3`
	font-weight: ${typography.weights.medium};
	font-size: ${typography.sizes.base};
	color: ${palette.grey900};
	margin: 0;
`;

const CategoryContainer = styled.div<{ isSelected?: boolean }>`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	padding: ${layout.tinySpace} 0;
	gap: ${layout.tinySpace};
`;

const CategoryHeader = styled(FlexRow)`
	gap: ${layout.tinySpace};
`;

const CategoryKPI = styled(FlexRow)`
	gap: ${layout.smallSpace};
`;

const CountText = styled.span`
	font-weight: ${typography.weights.semiBold};
	font-size: ${typography.sizes.xl};
	color: ${palette.grey900};
`;

const StyledTooltipIcon = styled(TooltipIcon)`
	display: flex;
`;

export const CategoryGroup: React.FC<CategoryKPIProps> = ({
	title,
	count,
	StatusIcon,
	helpText,
	isSelected = false,
	onClick,
}) => {
	return (
		<CategoryContainer isSelected={isSelected} onClick={onClick}>
			<CategoryHeader>
				<Subtitle>{title}</Subtitle>
				<Tooltip hoverTrigger={<StyledTooltipIcon size={12} color={palette.grey500} />}>
					{helpText}
				</Tooltip>
			</CategoryHeader>
			<CategoryKPI>
				{StatusIcon}
				<CountText>{count}</CountText>
			</CategoryKPI>
		</CategoryContainer>
	);
};

interface FiltersProps {
	expiredCount: number;
	expiringSoonCount: number;
	activeFilter: FilterType;
	onFilterChange: (filter: FilterType) => void;
}

const FiltersSection = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 48px;
	align-items: flex-start;
`;

export const CategoryFilters: React.FC<FiltersProps> = ({
	expiredCount,
	expiringSoonCount,
	activeFilter,
	onFilterChange,
}) => {
	return (
		<FiltersSection>
			<CategoryGroup
				title={t('Workspace:service_continuity.lease_expired_count')}
				count={expiredCount}
				StatusIcon={<AlertErrorIcon size={20} />}
				helpText={t('Workspace:service_continuity.lease_expired_help')}
				isSelected={activeFilter === 'expired'}
				onClick={() => onFilterChange('expired')}
			/>
			<CategoryGroup
				title={t('Workspace:service_continuity.lease_expiring_count')}
				count={expiringSoonCount}
				StatusIcon={<AlertWarningIcon size={20} />}
				helpText={t('Workspace:service_continuity.lease_expiring_help')}
				isSelected={activeFilter === 'expiring'}
				onClick={() => onFilterChange('expiring')}
			/>
		</FiltersSection>
	);
};
